import { useEffect, useMemo } from 'react';
import Graphic from '@arcgis/core/Graphic';
import Point from '@arcgis/core/geometry/Point';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import {
  useResourceSets,
  type ResourceSet,
} from 'components/IAP/hooks/resourceSetHooks';
import { applyHeleneResourcePopupToGraphic } from './cfa-demo/heleneResourcePopup';
import type { ArcgisMapView } from './utils/arcgisView';
import { isUscgSymbol, symbolToPictureMarkerMap } from './utils/uscgSymbology';
import {
  DEFAULT_ORG_ASSET_SYMBOL,
  ORG_ASSETS_LAYER_ID,
  ORG_ASSETS_LAYER_TITLE,
  selectOrgAssetPins,
  type OrgAssetPin,
} from './orgAssetPins';

const GRAPHIC_KEY_ATTRIBUTES = [
  'id',
  'resource_set_id',
  'resourceSetId',
  'name',
];

function pinSymbol(symbology: string) {
  const key = isUscgSymbol(symbology) ? symbology : DEFAULT_ORG_ASSET_SYMBOL;
  return (
    symbolToPictureMarkerMap[key] ??
    symbolToPictureMarkerMap[DEFAULT_ORG_ASSET_SYMBOL]
  );
}

function pinGraphic(pin: OrgAssetPin, resource: ResourceSet | undefined) {
  const graphic = new Graphic({
    geometry: new Point({
      longitude: pin.longitude,
      latitude: pin.latitude,
    }),
    symbol: pinSymbol(pin.symbology)?.clone(),
    attributes: {
      id: pin.resource_set_id,
      resource_set_id: pin.resource_set_id,
      name: pin.name,
    },
  });
  if (resource) applyHeleneResourcePopupToGraphic({ graphic, resource });
  return graphic;
}

/** Graphics layers other than the org-assets layer, including nested ones. */
function otherGraphicsLayers(mapView: ArcgisMapView): GraphicsLayer[] {
  const out: GraphicsLayer[] = [];
  const visit = (layer: __esri.Layer) => {
    if (layer instanceof GraphicsLayer && layer.id !== ORG_ASSETS_LAYER_ID) {
      out.push(layer);
    }
    if (layer instanceof GroupLayer) {
      for (const child of layer.layers.toArray()) visit(child);
    }
  };
  (mapView.map?.layers.toArray() ?? []).forEach(visit);
  return out;
}

/** Ids and names of asset-like graphics other layers already draw. */
function drawnGraphicKeys(mapView: ArcgisMapView): Set<string> {
  const keys = new Set<string>();
  for (const layer of otherGraphicsLayers(mapView)) {
    for (const graphic of layer.graphics.toArray()) {
      const attributes = (graphic.attributes ?? {}) as Record<string, unknown>;
      for (const attribute of GRAPHIC_KEY_ATTRIBUTES) {
        const value = attributes[attribute];
        if (value != null && String(value).trim() !== '') {
          keys.add(String(value).trim());
        }
      }
    }
  }
  return keys;
}

/**
 * Draw every located org asset that has no symbol on the map yet (for example
 * ones imported by PRATUS AI or added by hand) on an "Org assets" layer, using
 * each asset's symbology.
 */
export function useEnsureOrgAssetsLayer(mapView: ArcgisMapView | null) {
  const { data: resourceSets } = useResourceSets();
  const resources = useMemo(() => resourceSets ?? [], [resourceSets]);

  useEffect(() => {
    if (!mapView?.map) return;
    let layer = mapView.map.findLayerById(ORG_ASSETS_LAYER_ID) as
      | GraphicsLayer
      | undefined;
    if (!layer) {
      layer = new GraphicsLayer({
        id: ORG_ASSETS_LAYER_ID,
        title: ORG_ASSETS_LAYER_TITLE,
        listMode: 'show',
      });
      mapView.map.add(layer);
    }
    const target = layer;

    let lastSignature = '';
    const rebuild = () => {
      const pins = selectOrgAssetPins(resources, drawnGraphicKeys(mapView));
      const signature = JSON.stringify(pins);
      if (signature === lastSignature) return;
      lastSignature = signature;
      const byId = new Map(resources.map((r) => [r.resource_set_id, r]));
      target.removeAll();
      target.addMany(
        pins.map((pin) => pinGraphic(pin, byId.get(pin.resource_set_id)))
      );
    };

    let scheduled: ReturnType<typeof setTimeout> | null = null;
    const schedule = () => {
      if (scheduled) return;
      scheduled = setTimeout(() => {
        scheduled = null;
        rebuild();
      }, 0);
    };

    const handles: Array<{ remove: () => void }> = [];
    const attach = () => {
      handles.splice(0).forEach((handle) => handle.remove());
      for (const other of otherGraphicsLayers(mapView)) {
        handles.push(other.graphics.on('change', schedule));
      }
      if (mapView.map) {
        handles.push(
          mapView.map.allLayers.on('change', () => {
            attach();
            schedule();
          })
        );
      }
    };
    attach();
    rebuild();

    return () => {
      if (scheduled) clearTimeout(scheduled);
      handles.forEach((handle) => handle.remove());
    };
  }, [mapView, resources]);
}
